import React, { useEffect, useState } from "react";
import { Lock, Eye, CheckCircle2, Circle, Loader2 } from "lucide-react";
import { Toaster } from "sonner";
import LoadingScreen from "../Utils/LoadingScreen";
import TierBenefitsModal from "./TierBenefitsModal";
import useTier from "../../Sevices/useTier";
import useDriver from "../../Sevices/Driver Api/useDriver";

// Subtle accent colors per tier (no loud gradients)
const tierAccentMap = {
  Beginner: "bg-emerald-400",
  Intermediate: "bg-sky-400",
  Advanced: "bg-indigo-400",
  Expert: "bg-purple-400",
  Master: "bg-amber-400",
};

const TierBenefitsStepper = ({ tiers, tierBenefitsMap, loading }) => {
  // Map tierId -> tier meta (title, desc)
  const tierMetaById = tiers.reduce((acc, t) => {
    if (t.tierId) {
      acc[String(t.tierId)] = t;
    }
    return acc;
  }, {});

  // Build ordered list of tiers that actually have benefits
  const tierIdsWithBenefits = Object.keys(tierBenefitsMap).filter(
    (tierId) => (tierBenefitsMap[tierId] || []).length > 0,
  );

  const orderedTierIds = tierIdsWithBenefits.sort(
    (a, b) => Number(a) - Number(b),
  );

  if (loading) {
    return (
      <div className="mt-8 rounded-2xl border border-slate-200 bg-white px-6 py-8 flex items-center justify-center gap-3">
        <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
        <span className="text-sm text-slate-600">
          Loading tier benefits overview...
        </span>
      </div>
    );
  }

  if (!orderedTierIds.length) {
    return (
      <div className="mt-8 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-8 text-center text-sm text-slate-500">
        Tier benefits are not configured yet. Once benefits are added, an
        overview timeline will appear here.
      </div>
    );
  }

  return (
    <div className="mt-8 rounded-2xl border border-slate-200 bg-white px-6 py-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">
            Tier Benefits Overview
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            Timeline view of all tiers and their associated benefits.
          </p>
        </div>
      </div>

      <div className="relative">
        {/* vertical line */}
        <div className="absolute left-4 top-2 bottom-2 w-px bg-gradient-to-b from-slate-300 via-slate-200 to-slate-300 pointer-events-none" />

        <div className="space-y-6">
          {orderedTierIds.map((tierId, index) => {
            const benefits = tierBenefitsMap[tierId] || [];
            const meta = tierMetaById[tierId];
            const isLast = index === orderedTierIds.length - 1;

            return (
              <div key={tierId} className="relative flex gap-4">
                {/* Step icon */}
                <div className="relative z-10 flex flex-col items-center">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-50 border border-slate-300 shadow-sm">
                    {index === 0 ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                    ) : (
                      <Circle className="h-3 w-3 text-slate-500" />
                    )}
                  </div>
                  {!isLast && <div className="flex-1 w-px bg-transparent" />}
                </div>

                {/* Card */}
                <div className="flex-1 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <h4 className="text-sm font-semibold text-slate-900">
                        {meta?.tier_title ? meta.tier_title : `Tier #${tierId}`}
                      </h4>
                      {meta?.tier_description && (
                        <p className="text-xs text-slate-500 mt-0.5">
                          {meta.tier_description}
                        </p>
                      )}
                    </div>

                    <span className="rounded-full bg-white border border-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                      Tier ID: {tierId}
                    </span>
                  </div>

                  <div className="mt-3">
                    <p className="text-xs font-semibold text-slate-600 uppercase mb-1">
                      Benefits
                    </p>
                    <ul className="space-y-1.5">
                      {benefits.map((b) => (
                        <li
                          key={b.benefitId}
                          className="flex items-start gap-2 text-sm text-slate-800"
                        >
                          <span className="mt-[6px] h-1.5 w-1.5 rounded-full bg-slate-500" />
                          <span>{b.benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/**
 * Main Tier Section
 */
const TierSection = () => {
  const { FetchTierCall, FetchAllTierBenefits, addTierBenefit, deleteTierBenefit, Loading, benefitsLoading } = useTier();

  const { drivers, fetchDrivers } = useDriver();

  const [tiers, setTiers] = useState([]);
  const [filteredTiers, setFilteredTiers] = useState([]);
  const [filter, setFilter] = useState("All");
  const [selectedDriver, setSelectedDriver] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);

  // map: { [tierId]: [{benefitId, benefit}, ...] }
  const [tierBenefitsMap, setTierBenefitsMap] = useState({});

  // Initial Load
  useEffect(() => {
    fetchDrivers();
    FetchTierCall("", setTiers, setFilteredTiers);
    FetchAllTierBenefits(setTierBenefitsMap);
  }, []);

  // Tier Title Filter
  const handleFilterChange = (e) => {
    const value = e.target.value;
    setFilter(value);
    if (value === "All") {
      setFilteredTiers(tiers);
    } else {
      setFilteredTiers(tiers.filter((tier) => tier.tier_title === value));
    }
  };

  // Driver Filter
  const handleDriverChange = (e) => {
    const driverId = e.target.value;
    setSelectedDriver(driverId);
    FetchTierCall(driverId, setTiers, setFilteredTiers);
  };

  const openModal = (tier) => {
    setSelectedTier(tier);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedTier(null);
  };

  return (
    <section className="min-h-screen p-6">
      <Toaster />
      <div className="mx-auto max-w-6xl space-y-8">
        {/* Header + Filters */}
        <div className="flex flex-col flex-wrap items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div>
            <h2 className="text-2xl font-semibold text-slate-900">
              Driver Tiers
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Configure and review tier structure and benefits.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Tier Title Filter */}
            <select
              value={filter}
              onChange={handleFilterChange}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
            >
              <option value="All">All Tiers</option>
              <option value="Beginner">Beginner</option>
              <option value="Intermediate">Intermediate</option>
              <option value="Advanced">Advanced</option>
              <option value="Expert">Expert</option>
              <option value="Master">Master</option>
            </select>

            {/* Driver Filter */}
            <select
              value={selectedDriver}
              onChange={handleDriverChange}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
            >
              <option value="">Select Driver</option>
              {drivers.map((driver) => (
                <option
                  key={driver.id || driver.driverId}
                  value={driver.driverId}
                >
                  {driver.fullName}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Tier Cards */}
        {Loading ? (
          <LoadingScreen />
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {filteredTiers.map((tier) => {
              const accent = tierAccentMap[tier.tier_title] || "bg-slate-400";

              return (
                <div
                  key={tier.id || tier.tierId}
                  className="relative border border-slate-200 bg-orange/95 p-5 shadow-sm transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-md"
                  style={{ borderRadius: "0 1rem 1rem 0" }}
                >
                  {/* Accent strip */}
                  <div
                    className={`absolute inset-y-0 left-0 w-1 rounded-l-2xl ${accent}`}
                  />

                  <div className="mb-4 flex items-start justify-between gap-2">
                    <div className="space-y-1">
                      <h3 className="text-lg font-semibold text-slate-900">
                        {tier.tier_title}
                      </h3>
                      {tier.tier_description && (
                        <p className="text-sm text-slate-600">
                          {tier.tier_description}
                        </p>
                      )}
                    </div>

                    <div className="flex flex-col items-end gap-1">
                      <button
                        onClick={() => openModal(tier)}
                        className="flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100"
                      >
                        <Eye className="h-4 w-4" />
                        Benefits
                      </button>

                      <span className="mt-1 inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                        <Lock className="h-3 w-3" />
                        {tier.status}
                      </span>
                    </div>
                  </div>

                  <div className="mt-2 space-y-1.5">
                    <p className="text-xs font-semibold text-slate-500 uppercase">
                      Benefit summary
                    </p>
                    <p className="text-sm font-medium text-slate-800">
                      {tier.benefits || "Configured via benefits list"}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Stepper overview */}
        <TierBenefitsStepper
          tiers={tiers}
          tierBenefitsMap={tierBenefitsMap}
          loading={benefitsLoading}
        />

        {showModal && selectedTier && (
        <TierBenefitsModal
  tier={selectedTier}
  onClose={closeModal}
  addTierBenefit={addTierBenefit}
  deleteTierBenefit={deleteTierBenefit}
  refreshBenefits={() => FetchAllTierBenefits(setTierBenefitsMap)}
/>
        )}
      </div>
    </section>
  );
};

export default TierSection;
